import { Injectable } from '@angular/core';
import { HttpClientService } from '../core/services/http-client.service';
import { IpService } from '../core/services/ip.service';
import { HttpHeaders } from '@angular/common/http';
import { map, switchMap } from 'rxjs/operators';
import { from } from 'rxjs';
import { filenameDateStamp } from '../utils/filename-datestamp.util';
import { cacheBustProfileMedia } from '../utils/cache-bust-url.util';
import { VeteranProfile } from '../models';

@Injectable({
  providedIn: 'root',
})
export class StudentService {
  constructor(
    private httpClient: HttpClientService,
    private ipService: IpService
  ) {}

  getInboxMessage(studentId: string, type: string) {
    const newdate = filenameDateStamp();
    return this.httpClient
      .get(`/reconmessage/veteran/inbox/${studentId}/${type}?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  deleteMessage(messageInfo: any) {
    return this.httpClient.post(`/student/deletemessage`, messageInfo).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getInboxMessageCount(studentId: string) {
    const newdate = filenameDateStamp();
    return this.httpClient
      .get(`/reconmessage/veteran/inboxcount/${studentId}?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  saveSchool(saveinfo: any) {
    return this.httpClient.post(`/veteran/saved/school`, saveinfo).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getStudentMessageInfo(studentId: string) {
    const newdate = filenameDateStamp();
    return this.httpClient
      .get(
        `/student/messageinfo/${studentId}?dt=${newdate}
     `,
      )
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  // getVeteranCollegeReviewData(studentId: string) {
  //   const newdate = filenameDateStamp();
  //   return this.httpClient
  //     .get(`/veteran/college/reviewlist/${studentId}?dt=${newdate}`)
  //     .pipe(
  //       map((response: any) => {
  //         return response.data;
  //       }),
  //     );
  // }

  checkStudentMessageToCollege(studentId: string, collegeId: number) {
    const newdate = filenameDateStamp();
    return this.httpClient
      .get(
        `/student/check/collegemessage/${studentId}/${collegeId}?dt=${newdate}`,
      )
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getUserPhoneInfo(userId: string) {
    return this.httpClient.get(`/student/phonenumber/${userId}`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getMatchedParentOfCollege(studentId: any, collegeId: any) {
    const newdate = filenameDateStamp();
    return this.httpClient
      .get(`/college/matched-percent/${studentId}/${collegeId}?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getVeteranSecondaryBucket(studentId: string) {
    const newdate = filenameDateStamp();
    return this.httpClient
      .get(`/student/secondary/bucket/${studentId}?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getVeteranProfileAdmin(studentId: string) {
    const newdate = filenameDateStamp();
    return this.httpClient.get(`/admin/student/${studentId}?dt=${newdate}`).pipe(
      map((response: any) => {
        let results: VeteranProfile;
        results = response.data;
        return results;
      }),
    );
  }

  getVeteranProfileDetail(studentId: string) {
    const newdate = filenameDateStamp();
    return this.httpClient.get(`/student/${studentId}?dt=${newdate}`).pipe(
      map((response: any) => {
        let results: VeteranProfile;
        results = response.data;
        return results;
      }),
    );
  }

  getStudentSearchData(studentId: any) {
    const newdate = filenameDateStamp();
    return this.httpClient
      .get(`/student/matchpercent/searchdata/${studentId}?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getMatchedCollege(studentId: any) {
    const newdate = filenameDateStamp();

    return this.httpClient
      .get(`/matched/collegelist/${studentId}?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  checkMatchedCollege(matchData: any) {
    return this.httpClient
      .post(`/search/collegeprofile/checkcollege`, matchData)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  addPreferenceState(data: any) {
    return this.httpClient
      .post(`/search/match/save/preferencestate`, data)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  replyMessage(messageinfo: any) {
    // Get client IP and include it in the request headers
    return from(this.ipService.getClientIp()).pipe(
      switchMap((clientIp: string | null) => {
        // Build options with headers
        const options: any = {};
        
        if (clientIp) {
          const headers = new HttpHeaders().set('x-real-ip', clientIp);
          options.headers = headers;
        }
        
        return this.httpClient.post(`/student/replymessage`, messageinfo, options).pipe(
          map((response: any) => {
            return response.data;
          }),
        );
      })
    );
  }

  updatePhoneInfo(phoneInfo: any) {
    return this.httpClient.post(`/student/update/phonenumber`, phoneInfo).pipe(
      map((response: any) => {
        return response;
      }),
    );
  }

  sendMatchedRequestInfo(requestData: any) {
    return this.httpClient
      .post(`/student/requestinfo/matched`, requestData)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getBannerInfo(userId: string) {
    return this.httpClient.get(`/banner-info/${userId}`).pipe(
      map((response: any) => {
        return cacheBustProfileMedia(response.data);
      }),
    );
  }

  fetchMilitaryBases(userId: string) {
    return this.httpClient.get(`/student/military/bases/${userId}`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getMilitaryBaseInfo() {
    const searchUrl = `/military/bases`;
    return this.httpClient.get(searchUrl).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getRankByBranch(branchid: any) {
    const newdate = filenameDateStamp();

    return this.httpClient.get(`/rankbybranch/${branchid}?dt=${newdate}`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  fetchVeteranInsuranceAmount(userId: string) {
    return this.httpClient.get(`/student/insurance/amount/${userId}`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  fetchVeteranBahInfo(userId: string) {
    return this.httpClient.get(`/student/bah/register/${userId}`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  fetchVeteranBasInfo(userId: string) {
    return this.httpClient.get(`/student/bas/${userId}`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  fetchVeteranVADisabilityInfo(userId: string) {
    return this.httpClient.get(`/student/va-disability/${userId}`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  deactivateUserSetting(deactivateObj: any) {
    return this.httpClient.post(`/deactivate/user`, deactivateObj).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  fetchVeteranAwards(userId: string) {
    return this.httpClient.get(`/student/award/${userId}`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  fetchVeteranColleges(userId: string) {
    return this.httpClient.get(`/student/attended-school/${userId}`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  fetchVeteranDegreeInterest(userId: string) {
    return this.httpClient.get(`/student/degree-interest/${userId}`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  checkCareerreconUser(studentId: string) {
    const newdate = filenameDateStamp();
    return this.httpClient
      .get(`/student/checkcareeruser/${studentId}?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  uploadVeteranImage(veteranProfile: any) {
    return this.httpClient.post(`/student/update/photo`, veteranProfile).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  updateVeteran(veteranData: any, userId: string) {
    return this.httpClient.post(`/veteran/${userId}`, veteranData).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }
  updateCollegeEmailSettings(settingsInfo: any) {
    return this.httpClient.post(`/setting/email`, settingsInfo).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  updateCollegePasswordSetting(settingsInfo: any) {
    return this.httpClient.post(`/setting/resetpassword`, settingsInfo).pipe(
      map((respone: any) => {
        return respone.data;
      }),
    );
  }

  updatePrivacySetting(privacyInfo: any) {
    return this.httpClient.post(`/setting/privacy`, privacyInfo).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  updateVeteranMilitaryInfo(militaryInfo: any, studentId: string) {
    return this.httpClient
      .put(`/student/military-info/${studentId}`, militaryInfo)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  updateJstFilename(jstinfo: any) {
    return this.httpClient.post(`/student/update/jstfile`, jstinfo).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  updateVeteranEducation(educationInfo: any, studentId: string) {
    return this.httpClient
      .put(`/student/education-info/${studentId}`, educationInfo)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  updateMatchedCollegeData(data: any) {
    return this.httpClient
      .post(`/register/match/update/collegelist`, data)
      .pipe(
        map((response: any) => {
          return response;
        }),
      );
  }

  updateSavedCollegeData(data: any) {
    return this.httpClient
      .post(`/register/saved/update/collegelist`, data)
      .pipe(
        map((response: any) => {
          return response;
        }),
      );
  }

  getRegisterCollegeEmailData(data: any) {
    return this.httpClient.post(`/register/match/emaildata`, data).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getFeaturedColleges(studentId: string) {
    const newdate = filenameDateStamp();
    return this.httpClient
      .get(`/student/featureschool/${studentId}?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  savedSchoolListData(studentId: string) {
    return this.httpClient.get(`/veteran/saved/schoollist/${studentId}`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  deleteSavedCollege(data: any) {
    return this.httpClient.post(`/student/delete/savedschool`, data).pipe(
      map((response: any) => {
        return response;
      }),
    );
  }

  deleteMatchedCollege(data: any) {
    return this.httpClient.post(`/student/delete/matchedschool`, data).pipe(
      map((response: any) => {
        return response;
      }),
    );
  }

  getVeteranScholarshipData(studentId: string) {
    const newdate = filenameDateStamp();
    return this.httpClient
      .get(`/veteran/scholarship/detailinfo/${studentId}?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  sendScholarshipReportEmail(reportdata: any) {
    return this.httpClient.post(`/scholarship/report/email`, reportdata).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  manageScholarshipFollow(followdata: any) {
    return this.httpClient.post(`/scholarship/request`, followdata).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  unFollowScholarships(studentId: string, listId: any) {
    const newdate = filenameDateStamp();
    return this.httpClient
      .get(`/scholarship/unfollow/${studentId}/${listId}?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  fetchSchoolSpecificInfo(userId: string) {
    return this.httpClient.get(`/student/schoolspecific/info/${userId}`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getVeteranSecondaryBuckets(sid: any) {
    const newdate = filenameDateStamp();
    return this.httpClient
      .get(`/veteran/secondary/bucket/${sid}?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  counterSearchStudent(cdata: any) {
    return this.httpClient
      .post(`/usagestatistics/counter/searchstudent`, cdata)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getVeteranInboxMessageCount(studentid: any) {
    const newdate = filenameDateStamp();
    return this.httpClient
      .get('/reconmessage/veteran/inboxcount/' + studentid + '?dt=' + newdate)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getVeteranMatchedScholarship(filters: any) {
    return this.httpClient
      .post('/veteran/matched/scholarshiplist', filters)
      .pipe(
        map((response: any) => {
          return response;
        }),
      );
  }

  saveVeteranScholarshipInfo(scholarshipData: any) {
    return this.httpClient.post('/scholarship/add', scholarshipData).pipe(
      map((response: any) => {
        return response;
      }),
    );
  }

  getToggleFilterScholarship(filterData: any) {
    return this.httpClient
      .post('/veteran/filter/scholarshiplist', filterData)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  addUnsubscribeVeteran(unsubscribedata: any) {
    return this.httpClient
      .post('/veteran/email/unsubscribe', unsubscribedata)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  addUnsubscribeVeteranScholarship(unsubscribedata: any) {
    return this.httpClient
      .post('/veteran/scholarship/email/unsubscribe', unsubscribedata)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getVeteranInfoPercentages(uuid: string) {
    return this.httpClient.get('/student/dashboard/percentage/' + uuid).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getVeteranInfoStepperQuestions(type: string, uuid: string) {
    return this.httpClient.get('/student/emptydata/' + type + '/' + uuid).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  saveVeteranInfo(params: any) {
    return this.httpClient.post('/veteran/save/profiledata', params).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getVeteranOverallInfoStepperQuestions(uuid: string) {
    return this.httpClient.get('/student/overall/empty/' + uuid).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  saveVeteranOverallInfo(params: any) {
    return this.httpClient.post('/veteran/overall/profiledata', params).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getStudentReviews(studentId: string) {
    return this.httpClient.get('/student/reviews/' + studentId).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

   createGoogleSheet(payload: any) {
    return this.httpClient.post('/student/register/update-googleSheet', payload).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  // deleteReview(reviewId: string,studentId: string) {
  //   return this.httpClient.remove(`/student/${studentId}/review/${reviewId}`).pipe(
  //     map((response: any) => {
  //       return response.data;
  //     }),
  //   );
  // }
}
